import type { ApiClient } from './client';

export interface VoiceSampleRef {
  upload_id: string;
  source: 'file' | 'recording';
  script_no: number | null;
}

export interface UserVoice {
  id: number;
  voice_id: number | null;
  name: string;
  description: string;
  status: 'processing' | 'ready' | 'failed' | 'deleting';
  step: 'upload' | 'denoise' | 'verify' | 'train' | 'done' | null;
  verified: boolean;
  verification_state: string | null;
  provider_state: string | null;
  preview_state: string | null;
  denoise: boolean;
  error_code: string | null;
  samples_count: number;
  duration_ms: number;
  stats: { uses: number | null; symbols: number | null } | null;
  examples: Array<{
    lang: string;
    label: string;
    language_name: string;
    text: string;
    url: string;
    duration_ms: number;
  }>;
  preview_url: string | null;
  is_s2s: boolean;
  used_by_avatars: Array<{ id: number; name: string }> | null;
  orb: { from: string; to: string } | null;
  created_at: string;
  ready_at: string | null;
  version: number;
  generation: number;
  poll_after_ms: number | null;
}

export interface UserVoicesPage {
  policy?: ClonePolicy;
  items: UserVoice[];
  limit: number | null;
  used: number;
  entitlement_version: string | null;
  poll_after_ms: number | null;
  next_cursor?: string | null;
}

export interface CreateUserVoice {
  name: string;
  description: string;
  denoise: boolean;
  consent_version: string;
  samples: VoiceSampleRef[];
}

export interface ClonePolicy {
  creation_enabled: boolean;
  manage_enabled: boolean;
  consent_version: string | null;
  consent_text: string | null;
  min_duration_ms: number;
  min_sample_duration_ms: number;
  max_duration_ms: number;
  max_sample_bytes: number;
  max_samples: number;
}

/** Ключ создаётся в мастере один раз и сохраняется до известного результата. */
export const userVoices = (client: ApiClient) => ({
  list: (cursor?: string, signal?: AbortSignal) =>
    client.request<UserVoicesPage>('/app/voices', { query: { cursor }, signal }),
  get: (id: number, signal?: AbortSignal) =>
    client.request<UserVoice>(`/app/voices/${id}`, { signal }),
  preview: (id: number, signal?: AbortSignal) =>
    client.download(`/app/voices/${id}/preview`, signal),
  create: (body: CreateUserVoice, idempotencyKey: string) =>
    client.request<UserVoice>('/app/voices', { method: 'POST', body, idempotency: idempotencyKey }),
  update: (id: number, changes: { name?: string; description?: string }, version: number) =>
    client.request<UserVoice>(`/app/voices/${id}`, {
      method: 'PATCH', body: changes, ifMatch: `"${version}"`,
    }),
  remove: (id: number) =>
    client.request<void>(`/app/voices/${id}`, { method: 'DELETE' }),
  replaceSamples: (id: number, samples: VoiceSampleRef[], consentVersion: string, expectedVersion: number) =>
    client.request<UserVoice>(`/app/voices/${id}/samples`, {
      method: 'POST', body: { samples, consent_version: consentVersion, expected_version: expectedVersion },
      ifMatch: `"${expectedVersion}"`,
    }),
  retry: (id: number, changes: { name?: string; description?: string; denoise?: boolean },
    expectedVersion: number, idempotencyKey: string) =>
    client.request<UserVoice>(`/app/voices/${id}/retry`, {
      method: 'POST', body: { ...changes, expected_version: expectedVersion }, idempotency: idempotencyKey,
    }),
});
